import React from 'react';
import { Link } from 'react-router-dom';
import MakeAsyncFunction from 'react-redux-promise-listener';
import { Field as FField, Form as FForm } from 'react-final-form';
import styled from 'styled-components';
import { Row, Col, Form as AForm, Divider } from 'antd';

import { dispatch, promiseListener } from '../../store';
import { SIGNUP_REQUEST, SIGNUP_SUCCESS, SIGNUP_FAILURE } from '../../modules/sign-up/redux';
import { capitalize } from '../fields/renderFields';
import { InputControl, EmailInputControl, PasswordControl, CustomCheckboxInputControl } from '../fields/Fields';
import { StyledButton, StyledGoogleButton } from '../fields/Button';
import * as validations from '../../utils/validations';
import { handleShowToast } from '../../modules/notification/redux';
import LoadingSpinner from '../../lib/components/loadingSpinner';
import { FORM_ERROR } from 'final-form';
import GoogleIcon from '../../assets/google.png';
import env from '../../lib/env';
import { handleGoogleAuth } from '../../utils/auth';
import { FormattedMessage, useIntl } from 'react-intl';

const StyledOptInInput = styled.div`
  & .opt-in-field__checkbox {
    display: flex;
    float: left;
    margin-right: .25rem;
  }
`;

export const SignUpForm = ({ isLoading, initialValues }) => {
  const intl = useIntl();
  return (
    <MakeAsyncFunction
      listener={promiseListener}
      start={SIGNUP_REQUEST}
      resolve={SIGNUP_SUCCESS}
      reject={SIGNUP_FAILURE}
      getError={(action) => {
        const payload = action.payload;
        const error = payload.email ? payload.email : payload.orgName ? payload.orgName
          : payload[FORM_ERROR] ? payload[FORM_ERROR] : 'something went wrong';
        dispatch(handleShowToast({ message: error, type: 'error' }));
      }}
    >
      {onSubmit => (
        <FForm
          name='sign-up-form'
          style={{ textAlign: 'center' }}
          onSubmit={onSubmit}
          initialValues={initialValues}
          render={({
            handleSubmit,
            submitting,
            hasValidationErrors,
            hasSubmitErrors,
            dirtySinceLastSubmit,
            values
          }) => (
            <div>
              <LoadingSpinner spinning={isLoading}>
                <AForm onFinish={handleSubmit}>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <StyledGoogleButton
                        onClick={handleGoogleAuth}
                        type='button'
                      >
                        <img src={GoogleIcon} height={20} width={20} alt='' />
                        <FormattedMessage id='sign-up.main.signUpWithGoogle'/>
                      </StyledGoogleButton>
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <Divider orientation='center'>
                        <small><FormattedMessage id='sign-up.main.signUpWithEmail'/></small>
                      </Divider>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id='sign-up.main.firstName' />*</small>
                      <FField
                        name='firstName'
                        component={InputControl}
                        type='text'
                        placeholder={intl.formatMessage({ id: 'sign-up.main.firstName' })} // needs *
                        format={capitalize}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.minLength(2),
                          validations.maxLength(30)
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id='sign-up.main.lastName' />*</small>
                      <FField
                        name='lastName'
                        component={InputControl}
                        type='text'
                        placeholder={intl.formatMessage({ id: 'sign-up.main.lastName' })} // needs *
                        format={capitalize}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.minLength(2),
                          validations.maxLength(30)
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id='sign-up.main.email'/>*</small>
                      <FField
                        name='email'
                        component={EmailInputControl}
                        type='text'
                        placeholder={intl.formatMessage({ id: 'sign-up.main.email' })} // needs *
                        disabled={initialValues.email !== ''}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.email
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id='sign-up.main.school' />*</small>
                      <FField
                        name='orgName'
                        component={InputControl}
                        type='text'
                        placeholder={intl.formatMessage({ id: 'sign-up.main.schoolHelper' })}
                        disabled={initialValues.orgName !== ''}
                        format={capitalize}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.minLength(4),
                          validations.maxLength(50)
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id='sign-up.main.password'/>*</small>
                      <FField
                        name='password'
                        component={PasswordControl}
                        type='text'
                        placeholder={intl.formatMessage({ id: 'sign-up.main.password' })}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.minLength(6)
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col lg={12} sm={20} xs={24}>
                      <small style={{ display: 'flex', justifyContent: 'left', marginBottom: 5 }}><FormattedMessage id='sign-up.main.sign-in-helper'/> <Link to='/sign-in' style={{ marginLeft: 2, textDecoration: 'underline' }}><FormattedMessage id='sign-up.main.sign-in-link'/> </Link></small>
                      <small style={{ display: 'block', justifyContent: 'left', marginBottom: 5 }}>
                        <FormattedMessage id='sign-up.main.conditionsHelper' />
                        <Link to={{ pathname: `https://app.termly.io/policy-viewer/policy.html?policyUUID=${env.REACT_APP_TERMLY_TCP_UUID}` }} target='_blank' style={{ textDecoration: 'underline' }}><FormattedMessage id='sign-up.main.conditions-link' /></Link>
                        <Link to={{ pathname: `https://app.termly.io/policy-viewer/policy.html?policyUUID=${env.REACT_APP_TERMLY_PP_UUID}` }} target='_blank' style={{ textDecoration: 'underline' }}> <FormattedMessage id='sign-up.main.policy-link' /></Link>
                        <Link to={{ pathname: `https://app.termly.io/policy-viewer/policy.html?policyUUID=${env.REACT_APP_TERMLY_AUP_UUID}` }} target='_blank' style={{ textDecoration: 'underline' }}> <FormattedMessage id='sign-up.main.acceptable-use-link' /></Link> <FormattedMessage id='sign-up.main.and' />
                        <Link to={{ pathname: `https://app.termly.io/policy-viewer/policy.html?policyUUID=${env.REACT_APP_TERMLY_CP_UUID}` }} target='_blank' style={{ marginLeft: 2, textDecoration: 'underline' }}><FormattedMessage id='sign-up.main.cookies-link' /></Link>
                        <FormattedMessage id='sign-up.main.sms'/>
                      </small>
                      <small style={{ display: 'block', justifyContent: 'left', marginBottom: 20 }}>
                        <StyledOptInInput>
                          <FField
                            name='marketingOptIn'
                            component={CustomCheckboxInputControl}
                            type='checkbox'
                            className='opt-in-field__checkbox'
                            initialValue
                          />

                          <FormattedMessage id='sign-up.main.learnMoreHelper' /> <Link to='/#' style={{ textDecoration: 'underline' }}><FormattedMessage id='sign-up.main.newsletter' />.</Link>
                        </StyledOptInInput>
                      </small>

                      <StyledButton
                        size='large'
                        type='primary'
                        htmlType='submit'
                        disabled={submitting ||
                        ((hasSubmitErrors || hasValidationErrors) && !dirtySinceLastSubmit)}
                        loading={isLoading || submitting}
                        block
                      >
                        <FormattedMessage id='sign-up.main.sign-up-button'/>
                      </StyledButton>
                    </Col>
                  </Row>
                </AForm>
              </LoadingSpinner>
            </div>
          )}
        />
      )}
    </MakeAsyncFunction>
  );
};
